// What a selected square offers, as a value (PLAN-UI §28.3, U6; relocation-map
// decisions 1, 2 and 7).
//
// **One card, and everything is inline in it** (designer ruling, U6 second
// review, 2026-09-02). The first review's shape was a card plus a `···` door
// onto a second panel of dense policy; the designer does not want the door.
// Actions and dense policy may take space in the card — a player looking at a
// machine is looking at one machine — and a second surface was a click, a
// placement problem and a thing to discover before the store's taps could be
// touched at all. So the model is three fields: the **inspector** readouts, the
// **controls** row, and the **sections** drawn under it.
//
// The two casualties are the door and the thing that was only a door:
// `action-more` is gone, and so is `save-for` — the `saving` section's five
// rank tiles *are* the save-for control now, and what the old headline said
// (the coalition-priced bill, ready or short) rides on the lit tile's own
// hover card.
//
// **Nothing of the selection floats over the board** (first review, kept). The
// board-anchored action bar covered the squares it was about and re-projected
// every frame the camera moved, so the selection has one home and the controls
// are drawn *inside* the card, under the readout rows. `action-bar` is still
// that row's test id and the smokes read it.
//
// **A control carries its own state visually, and its words live on a hover
// card** (same review). What a lit Dump toggle says, a `Policy` row does not
// repeat — so the readout fields that restated a control are gone, and
// `policy`, `saving` and `kingLine` moved onto the items as `SelectionItem`'s
// `card`. Captions went with them: the visual state is the caption. Fewer words
// in the interface is the standing instruction.
//
// Pure, like `hotbar.ts` and `orders.ts`: no React, no DOM, no `@mui`. One
// component draws it and it decides nothing.
//
// **Every test id and every sentence from the shelf's `Selection` survives.**
// That is the API this file guarantees — `selection.test.ts` asserts the ids
// against the fixtures that exercise them — because a relocation that quietly
// drops a control is indistinguishable from a bug in the control.
//
// Two permissions, and conflating them cost the player half their thinking
// time (the shelf's own note, kept). What changed at §29 U1 is that they no
// longer disagree about the clock:
//
//   **Policy** — what a machine is saving for, which way it points its pawns,
//   whether it holds or passes on, what a king is posted to, a store's taps, a
//   turret's aim — costs no tempo and is **turn-bound** (`requireBuilder`).
//   It was not, and the reversal is what gives undo a turn that holds one
//   side's commands.
//
//   **Actions** — dig, hand off, raise, climb, craft — cost the turn as well as
//   waiting for it. Taking a building down is one too and is *not here*: the
//   rail's Discard tool is its only door (`interaction/discard.ts`).
//
// Both are gated on `settable` now, and the difference that survives is what
// happens off-turn: policy controls are still *drawn*, dimmed, carrying the
// reason on their card. **Disabled, never removed** — a control that vanishes
// on the opponent's turn is a control the player cannot find or ask about.

import { gearBorneAt, gearName, gearPowered } from "src/engine/rules/campaign/equipment";
import { type Color, KING, type PieceType, pieceType } from "src/engine/rules/board";
import type { Command, GameState } from "src/engine/rules/commands";
import {
  areEnemies,
  livingSides,
  blowOf,
  healthOf,
  maxHealthOf,
  platingAt,
  pieceAt
} from "src/engine/rules/position";
import { billFor, materialName } from "src/engine/rules/materials";
import {
  GRADE_NAMES,
  type Grade,
  MINTABLE,
  PIECE_NAMES,
  PIECE_VALUE,
  SURRENDER_VALUE
} from "src/engine/rules/rules";
import { pieceLabel } from "src/engine/rules/naming";
import { canForge, type ForgeError } from "src/engine/rules/industry/machines";
import { fundingOptions } from "src/engine/rules/industry/coalition";
import { bufferAtLeast, structureAt, TASK_REFINE, WELL } from "src/engine/rules/industry/state";
import { nextSeizeRound, seizeError, type SeizeError } from "src/engine/rules/campaign/scepter";
import { STRUCTURE_NAMES } from "src/engine/rules/industry/structures";

import type { PieceIcon } from "src/api/chrome";
import { armyView } from "./armyView";
import { REFUSAL } from "src/engine/session/refusals";
import { canAct } from "src/engine/session/turn";
import { squareLabel } from "src/engine/session/gridLabels";
import { PIECE_GLYPH, PIECE_ICON } from "./glyphs";
import type { TileRow } from "./hotbar";
import { type BadgePip, type PieceBadge } from "./pieceBadges";
import { DIRECTION_NAMES, DIRECTIONS } from "src/shared/board";

/**
 * A free action on the selection, in the interaction hook's own shape.
 *
 * Declared here rather than imported from a component: this file may not
 * depend on one, and the shape is structural, so `board_.actions` passes
 * straight in. It is the hook's shape
 * and not `BadgeAction`'s because only the hook knows about `done`, which is
 * "you already did this", information rather than a missing button.
 */
export interface SelectionAction {
  kind: "mine" | "load-belt";
  label: string;
  /** The square the action acts on, when it is not the selected one. */
  square?: number;
  /** Already spent this turn: shown, and switched off. */
  done?: boolean;
}

/** What pressing a control does. The component owns the wiring, not the rule. */
export type SelectionRun =
  /** → `hook.act(action, square)`. */
  | { kind: "act"; action: "mine" | "load-belt"; square: number }
  /** → `dispatch`. Owner-bound policy: never turn-bound, always carries `owner`. */
  | { kind: "command"; command: Command }
  /**
   * → `hook.upgradePiece`. `variant` is what tells a sidegrade from a rung —
   * `target` is the rank either way, and the reducer knows they are different
   * acts.
   */
  | { kind: "upgrade"; square: number; target: PieceType; variant?: number }
  /** → `hook.fusePieces` (§31 P4): the parts, anchor first, and the row. */
  | { kind: "fuse"; parts: number[]; into: number }
  /** → `hook.beginSacrifice`. Arms the power; the board lights the bodies. */
  | { kind: "sacrifice" };

/**
 * What a control says when it is hovered, and how it reads while it waits.
 *
 * One shape for both, because they are one decision (designer ruling, U6
 * review): `state` is the visual half — the `save-for` glyph lights brand when
 * its coalition can pay, rust-leaning when it is short, dim when nothing is
 * set — and the card is what that state spells out for anybody who asks.
 * **Native `title` is out** everywhere in the selection UI; the kit's
 * `HoverCard` is the one surface, so this is the only place a control's words
 * live.
 */
export interface SelectionCard {
  /** The heading — what the control is. */
  title: string;
  /** The reason it is off, or the rule it obeys. One or two sentences. */
  body: string;
  /** A bill, drawn as swatched rows (`unitRows`' `Price`). Never words. */
  rows?: TileRow[];
  /** How the control itself reads at rest. Absent is an ordinary control. */
  state?: "ready" | "short" | "unset";
}

/** One control, wherever it is drawn. */
export interface SelectionItem {
  /** The `data-testid`, verbatim from the shelf. */
  id: string;
  /** Short: the mock's buttons are one or two words. */
  label: string;
  /** `ACTION_GLYPH` / `PIECE_GLYPH` where the shelf had one. */
  glyph?: string;
  /** `PIECE_ICON` where the kit draws the rank; drawn in place of `glyph`. */
  icon?: PieceIcon;
  /** `aria-pressed`. */
  on?: boolean;
  enabled: boolean;
  /** Why it is off, or the rule it obeys. The card's body is built from it. */
  reason: string | null;
  /** Everything the control says and the state it says it in. */
  card?: SelectionCard;
  run: SelectionRun;
}

/** The three numbers a trade is decided on (§27), as the shelf's `PieceStats`. */
export interface SelectionStats {
  blow: number;
  /** "3/5", or "—" for a last king, which falls to any blow. */
  health: string;
  /** "5 scrap", or "9 on surrender" for a king. */
  leaves: string;
  /**
   * Plate still to chew through (`Position.plating`), 0 for none — and the row
   * is not drawn at nought, like Holds. A number in a row rather than a
   * sentence in a pip, which is the U6 shape for a readout.
   */
  armour: number;
}

/**
 * The inspector card: everything the selection has to say about itself.
 *
 * **Readouts only.** The controls are `controls`, drawn in a row under these,
 * and each one carries its own state — so the three fields that only restated
 * a control (`policy`, `saving`, `kingLine`) are gone. What a lit Dump toggle
 * says, a `Policy` row does not repeat; what a foundry is saving for is the lit
 * rank tile in the `saving` section and the card on it.
 */
export interface InspectorModel {
  name: string;
  /** `squareLabel`. */
  square: string;
  owner: "yours" | "ally" | "enemy";
  pips: BadgePip[];
  detail: string;
  /** Null for a building — there is no piece standing on its square. */
  stats: SelectionStats | null;
  /** Swatch counts, never sentences. Dearest slot first. */
  holdings: { slot: number; count: number }[];
  progress: { fraction: number; ready: boolean } | null;
  /** Whose army this is, on somebody else's piece only. */
  army: { army: string; profile: string | null } | null;
  /** "Not yours." / "Wait for your turn." */
  note: string | null;
  /** A Monarch's gear (§31 A0) and whether each works; absent when none. */
  equipment?: { name: string; powered: boolean }[];
}

/** One block under the controls row, drawn inline in the card. */
export type ContextSection =
  | { kind: "saving"; title: string; options: SelectionItem[] }
  | {
      kind: "taps";
      title: string;
      rows: {
        grade: number;
        name: string;
        count: number;
        accept: SelectionItem;
        offer: SelectionItem;
      }[];
    }
  /**
   * A turret's three stats. `stat` is the row's own name, drawn beside it —
   * carried on the section's row rather than as a `caption` on the item, the
   * way `climb` carries its `note`, because captions came off the
   * items at the U6 review and this one is the section's layout, not a control.
   */
  | { kind: "turret"; title: string; rows: (SelectionItem & { stat: string })[] }
  | { kind: "climb"; title: string; tiles: (SelectionItem & { note: string })[] }
  | { kind: "sacrifice"; item: SelectionItem };

export interface SelectionModel {
  inspector: InspectorModel;
  /** Empty → the component draws no controls row. */
  controls: SelectionItem[];
  /** Empty → the card is readouts and controls. Drawn inline, in order. */
  sections: ContextSection[];
}

export interface SelectionInput {
  state: GameState;
  square: number;
  localSide: Color;
  badge: PieceBadge;
  /** The hook's `actions` for the square. */
  actions: readonly SelectionAction[];
  /** The run's held power ids, as `GameShell` computes them. */
  powers: readonly string[];
  /**
   * The player is looking back through history (§29 U4).
   *
   * Everything settable goes off, with the view as the reason: the controls
   * describe a board the game has moved on from, and a policy set against one
   * would land on a position the player cannot see.
   */
  viewing?: boolean;
  /** The scenario screen's per-side profile override. `armyView`'s. */
  profiles?: readonly (string | undefined)[];
  machinePlays: boolean;
}

/**
 * What each call to action looks like — the shelf's table, kept.
 *
 * Keyed on the *kind*, not the label: the label carries a count and changes
 * ("Mine (3 left)"), and a picture that changed with it would read as a
 * different action every turn.
 */
export const ACTION_GLYPH: Record<string, string> = {
  mine: "⛏",
  "load-belt": "➤",
  raise: "☠"
};

/** The two notes the shelf's third column ends with. */
const NOT_YOURS = "Not yours.";
const OFF_TURN = REFUSAL["not-your-turn"];
const LOOKING_BACK = REFUSAL["viewing-history"];

/** The hints those two notes carry, reused as the reason on a gated control. */
const POLICY_RULE =
  "Machine policy is owner-bound: you can read anybody's factory, and only set your own.";
const TURN_RULE =
  "Everything waits for your turn — policy as well as digging, handing off and " +
  "forging. A turn holds one side's actions, which is what lets you take them back.";
/** The same rule's place while a step of history is on screen (§29 U4). */
const VIEW_RULE =
  "This is an earlier position, kept for you to read. Press Latest to go back to " +
  "the live board and the controls come with you.";

/**
 * The rule behind each note, for the title the component hangs on it.
 *
 * Both sentences were `Legend` hints in the shelf and neither has anywhere else
 * to live now that the note is one line of the inspector.
 */
export const NOTE_HINT: Record<string, string> = {
  [NOT_YOURS]: POLICY_RULE,
  [OFF_TURN]: TURN_RULE,
  // §29 U4. Every note needs a rule behind it or its hover card is empty,
  // which is the silent half of a silent no-op.
  [LOOKING_BACK]: VIEW_RULE
};

/** The king-posting rule, from the shelf's own Legend. */
const POSTING_RULE =
  "A king does one job a turn and gets faster at the one it keeps — capacity rises " +
  "each turn on the same job and resets when it switches, or when the king moves. " +
  "Posting it is absolute, even with nothing to do yet.";

const CLIMB_RULE =
  "Costs no move. Paid, with 2 fuel points, by one source: a piece of yours that " +
  "forges beside it, or an Armory within 2 out of your nearest stores. The piece " +
  "keeps its rank, square, buffers and posting; only what it is changes.";

const FUSE_RULE =
  "Costs no move. Needs the Fusion Chamber and the Signet of Union. Paid, with 2 fuel " +
  "points, by one source: a forger touching every part, or an Armory or the Chamber " +
  "within 2 of them all. The first part stays and becomes the fused piece; the others " +
  "are consumed, their cargo poured into it.";

const TURRET_RULE =
  "Turning a Kinetic or Cannon Turret costs a move. Its cone of fire runs out from " +
  "its facing; it fires at your End Turn and keeps the rest as overwatch.";

const TAPS_RULE =
  "Two switches per grade — what the store accepts, and what it offers back to the " +
  "network. All four combinations are useful: a stockpile, a buffer, an open tap, or " +
  "sealed.";

/** The power's own text, plus the gesture that starts it. */
const SACRIFICE_RULE =
  "Take one of your pieces apart beside your altar or your Kris-bearer. Half its bill " +
  "comes back, at the grades it was made of, and a bonus factory tick. Once a turn. " +
  "Click the piece to take apart.";

/** `pieceBadges`' `forgeRefusal`, minus the poverty branch — see `shortfall`. */
const FORGE_SAYS: Record<Exclude<ForgeError, "too-poor">, string> = {
  "no-room": "No free square beside it.",
  "king-busy": "This king has already worked this turn — it gets one job a turn.",
  "not-mintable": "A foundry cannot make one of those.",
  "not-your-foundry": "Not your foundry.",
  "no-foundry": "No foundry of yours touches that square.",
  "no-coalition": "The foundries beside that square cannot pay for it between them."
};

/**
 * What an unlit rank tile says: the name, the bill, and four words.
 *
 * **Fewer words is the standing instruction** (designer ruling, U6 second
 * review). This was two clauses of rules lawyering — that the pick is policy,
 * never turn-bound, never refused for poverty — on all five tiles, every
 * hover, for a control whose whole behaviour is that clicking it works. The
 * rule is true and it is not news; the price is the thing being asked about,
 * and the swatched rows under this line are the price.
 */
const SAVING_RULE = "Stock up for this.";

/**
 * What a foundry is still short of, priced across the **whole coalition**.
 *
 * The owed item from §28.4: `pieceBadges`' `too-poor` sentence counts the
 * foundry's *own* hopper as "have", which under-reports beside a rich
 * neighbour — two rooks with half a knight each are told twice that they are
 * half a knight short, while between them they can pay. The tile that showed
 * that sentence died at U4; this is where it must not come back.
 *
 * So the bill is weighed at every square this foundry could lead a forge onto,
 * against everybody who would chip in for that square, and the sentence names
 * the **best** one. `fundingOptions` is the same sweep `fundableSquares`
 * filters, so the square named here is a square the reducer would accept if the
 * metal turned up.
 */
function shortfall(
  state: GameState,
  square: number,
  owner: Color,
  type: PieceType
): string | null {
  const bill = billFor(type);
  let best: { row: (typeof bill)[number]; missing: number }[] | null = null;
  let least = Infinity;

  for (const option of fundingOptions(state.position, state.industry, square, owner)) {
    const gaps = bill.map((row) => {
      let have = 0;
      for (const funder of option.funders) {
        have += bufferAtLeast(state.industry, funder, row.grade as Grade, "in", row.line);
      }
      return { row, missing: Math.max(0, row.count - have) };
    });
    const total = gaps.reduce((sum, gap) => sum + gap.missing, 0);
    if (total < least) {
      least = total;
      best = gaps;
    }
  }

  if (!best || least === 0) return null;
  // The shelf's own wording, one clause per row that is short. A bill carries
  // at most one row per line, so this is never more than three.
  return best
    .filter((gap) => gap.missing > 0)
    .map(
      (gap) =>
        `${gap.missing} more of ${materialName(gap.row.line, gap.row.grade)} or better.`
    )
    .join(" ");
}

/**
 * A foundry's recipe state, as the **lit rank tile's** own card.
 *
 * This was a pair of inspector rows — "Saving for" with the bill, and the
 * shortfall sentence under it — and then, for one afternoon, a `save-for`
 * headline control that was really a door onto the picker. The second review
 * deleted the door: the picker is inline now, so the tile that is already lit
 * is the readout, and this is what it says when it is asked.
 */
function savingCard(
  state: GameState,
  square: number,
  badge: PieceBadge,
  target: PieceType
): SelectionCard {
  const why = canForge(state.position, state.industry, square, badge.owner, target);
  const short =
    why === null
      ? null
      : why === "too-poor"
      ? shortfall(state, square, badge.owner, target) ?? FORGE_SAYS["no-coalition"]
      : FORGE_SAYS[why];
  return {
    title: `Saving for a ${PIECE_NAMES[target]}`,
    // Ready is said as plainly as short is: the whole point of the tone is
    // that a glance answers it, and the card is what confirms the glance.
    body: short ?? "The foundries beside it can pay for one now.",
    rows: billFor(target).map((row) => ({
      count: row.count,
      line: row.line,
      grade: row.grade
    })),
    state: short === null ? "ready" : "short"
  };
}

/** The shelf's `PieceStats`, as three strings. Null on a building. */
function statsOf(state: GameState, square: number): SelectionStats | null {
  const piece = pieceAt(state.position, square);
  if (!piece) return null;
  const type = pieceType(piece);
  const max = maxHealthOf(state.position, square);
  const drops = PIECE_VALUE[type];
  return {
    blow: blowOf(state.position, square),
    armour: platingAt(state.position, square),
    // A last king's health is nought, stated rather than derived — and it is
    // drawn as a dash, because "0/0" reads as a piece already dead.
    health: max > 0 ? `${healthOf(state.position, square)}/${max}` : "—",
    leaves:
      drops > 0
        ? `${drops} scrap`
        : type === KING
        ? `${SURRENDER_VALUE[KING]} on surrender`
        : "—"
  };
}

/** The jobs a king can be *posted* to. Forging is deliberately not one. */
const KING_TASKS = [{ id: TASK_REFINE, label: "Refine" }];
/** The same jobs as participles, for the posting control's own card. */
const KING_DOING: Record<number, string> = { [TASK_REFINE]: "Refining" };

/**
 * "Refining · 2 at a time" — what the king is actually doing, and how fast.
 *
 * This was an inspector row of its own ("This turn"). The U6 review moved it
 * onto the Refine control, where it is the heading of that control's card: it
 * is a fact *about the posting*, and a card the posting is lit beside says it
 * without spending a row on prose.
 */
function kingLine(badge: PieceBadge): string | null {
  if (badge.kingDoing === undefined) return null;
  return `${KING_DOING[badge.kingDoing] ?? "Waiting"}${
    badge.kingTempo ? ` · ${badge.kingTempo} at a time` : ""
  }`;
}

/**
 * Everything the inspector card draws — readouts, controls and sections.
 *
 * Derived from the board every call and nothing cached: the caller memoises on
 * the state, exactly as the shelf's badges do.
 */
export function selectionModel(input: SelectionInput): SelectionModel {
  const { state, square, localSide, badge } = input;
  const yours = badge.owner === localSide;
  const settable = yours && canAct(state, localSide, input.viewing);
  /** Why everything is off, said on every card that is: the clock or the view. */
  const rule = input.viewing ? VIEW_RULE : TURN_RULE;
  // Policy is owner-bound, and this is whether it is *drawn* — it is drawn on
  // anybody's clock and enabled only on yours (§29 U1). The name is the
  // shelf's.
  const yourPolicy = yours;

  const piece = pieceAt(state.position, square);
  // A building is not a piece and must not be labelled one.
  const structure = structureAt(state.industry, square);
  const name = piece
    ? pieceLabel(state.position, square)
    : structure
    ? STRUCTURE_NAMES[structure.kind]
    : "Piece";

  const army = yours
    ? null
    : armyView(state, badge.owner, input.profiles, input.machinePlays);

  const borne = gearBorneAt(state.position, state.industry, square);
  const inspector: InspectorModel = {
    ...(borne.length > 0
      ? {
          equipment: borne.map((gear) => ({
            name: gearName(gear.kind),
            powered: gearPowered(state.industry, gear)
          }))
        }
      : {}),
    name,
    square: squareLabel(square),
    owner: yours
      ? "yours"
      : areEnemies(state.position, localSide, badge.owner)
      ? "enemy"
      : "ally",
    pips: badge.pips,
    detail: badge.detail,
    stats: statsOf(state, square),
    holdings: badge.materials,
    progress:
      badge.progress === undefined
        ? null
        : { fraction: badge.progress, ready: Boolean(badge.ready) },
    army: army ? { army: army.army, profile: army.profile ?? null } : null,
    note: !yours
      ? NOT_YOURS
      : settable
        ? null
        : input.viewing
          ? LOOKING_BACK
          : OFF_TURN
  };

  const gates = { yours, yourPolicy, settable, rule };
  return {
    inspector,
    controls: controlsOf(input, gates),
    sections: sectionsOf(input, gates)
  };
}

interface Gates {
  yours: boolean;
  yourPolicy: boolean;
  settable: boolean;
  /** The one sentence every off control says — the clock, or the view (§29 U4). */
  rule: string;
}

/**
 * Dim a policy control off-turn, keeping it drawn (§29 U1).
 *
 * **Disabled, never removed**: the control stays where the player found it,
 * hoverable, and the card says why — an off control's reason is the whole of
 * its content (`Control.tsx`'s ruling). One helper rather than the same
 * ternary on six controls, so a seventh cannot be added without the gate.
 */
function onClock<T extends { enabled: boolean; reason: string | null; card?: SelectionCard }>(
  item: T,
  settable: boolean,
  rule: string
): T {
  if (settable) return item;
  return {
    ...item,
    enabled: false,
    reason: rule,
    ...(item.card ? { card: { ...item.card, body: rule } } : {})
  };
}

/**
 * The controls row: free actions and one or two headline policies.
 *
 * Order is fixed and is the reading order of the shelf's third column — what
 * you can *do* first, then what the machine is standing under. It is drawn
 * inside the inspector card, under the readouts (designer ruling, U6 review);
 * the `action-bar` test id is what survives of the floating bar.
 *
 * **No `···` and no `save-for`** (second review): every section is inline
 * under this row, so a door onto them would be a click for nothing, and the
 * recipe headline was only ever that door wearing a glyph.
 */
function controlsOf(input: SelectionInput, gates: Gates): SelectionItem[] {
  const { badge, square, localSide, actions } = input;
  const { yours, yourPolicy, settable, rule } = gates;
  const out: SelectionItem[] = [];

  // Nothing of yours is a control at all — reading somebody's factory is the
  // inspector's job.
  if (!yours) return out;

  for (const action of actions) {
    const why = !settable ? rule : action.done ? action.label : null;
    out.push({
      id: `action-${action.kind}`,
      // The label carries the done-state exactly as the hook writes it —
      // "Mined this turn" is information, and a missing button is not.
      label: action.label,
      glyph: ACTION_GLYPH[action.kind],
      enabled: settable && !action.done,
      reason: why,
      card: { title: action.label, body: why ?? rule },
      run: { kind: "act", action: action.kind, square: action.square ?? square }
    });
  }

  // **The Oil-Stone Scepter** (§31 A4): on your Sacred Well, one control per
  // living enemy faction — play its next turn. Drawn whenever the Well is
  // yours, enabled only while `seizeError` agrees; the card says why not.
  if (structureAt(input.state.industry, square)?.kind === WELL) {
    out.push(...seizeControls(input, settable, rule));
  }

  if (yourPolicy && badge.kingTask !== undefined) {
    for (const task of KING_TASKS) {
      out.push(onClock({
        id: `king-task-${task.id}`,
        label: task.label,
        on: badge.kingTask === task.id,
        enabled: true,
        reason: POSTING_RULE,
        // The tempo line is the heading, which is where the inspector's old
        // "This turn · Refining · 2 at a time" row went.
        card: { title: kingLine(badge) ?? task.label, body: POSTING_RULE },
        run: {
          kind: "command",
          command: {
            type: "set-king-task",
            square,
            // Clicking the posting it already has stands it down, which is the
            // only way to say "decide for me".
            task: badge.kingTask === task.id ? null : task.id,
            owner: localSide
          }
        }
      }, settable, rule));
    }
  }

  if (yourPolicy && badge.routes) {
    // The shelf's two labels, kept on the card: the badge reports the
    // *setting*, not the derived behaviour, and a lit toggle is the readout.
    const doing = badge.dumps ? "Dumping spare material" : "Keeping material";
    out.push(onClock({
      id: "toggle-dump",
      label: "Dump",
      on: badge.dumps,
      enabled: true,
      reason: doing,
      card: { title: "Dump", body: doing },
      run: {
        kind: "command",
        command: { type: "set-dump", square, dump: !badge.dumps, owner: localSide }
      }
    }, settable, rule));
  }

  return out;
}

const SEIZE_RULE =
  "Play this faction's next turn yourself — its legal moves only, then End Turn. " +
  "Once every five rounds while this Well and its Oil-Stone Scepter stand.";

const SEIZE_SAYS: Record<SeizeError, string> = {
  "not-a-run": "Only in a run.",
  "no-power": "The Oil-Stone Scepter is not borne by a Monarch of yours, or this Well has lost it.",
  "not-an-enemy": "Not a living enemy faction.",
  "too-soon": "The Scepter is resting."
};

/** `seize-<side>` per living enemy faction, for the Sacred Well's card. */
function seizeControls(input: SelectionInput, settable: boolean, rule: string): SelectionItem[] {
  const { state, localSide } = input;
  const out: SelectionItem[] = [];
  for (const side of livingSides(state.position)) {
    if (side === localSide || !areEnemies(state.position, localSide, side)) continue;
    const why = seizeError(state, localSide, side);
    const next = nextSeizeRound(state, localSide);
    const reason =
      why === "too-soon"
        ? `${SEIZE_SAYS[why]} Next in round ${next}.`
        : why
          ? SEIZE_SAYS[why]
          : SEIZE_RULE;
    const label = `Seize ${armyView(state, side, input.profiles, input.machinePlays).army} (${side + 1})`;
    out.push(onClock({
      id: `seize-${side}`,
      label,
      enabled: why === null,
      reason,
      card: { title: label, body: reason },
      run: { kind: "command", command: { type: "seize", owner: localSide, target: side } }
    }, settable, rule));
  }
  return out;
}

/** The sections under the row: dense policy, inline, in one fixed order. */
function sectionsOf(input: SelectionInput, gates: Gates): ContextSection[] {
  const { state, badge, square, localSide, powers } = input;
  const { yours, yourPolicy, settable, rule } = gates;
  const out: ContextSection[] = [];

  if (yourPolicy && badge.recipes && badge.recipes.length > 0) {
    out.push({
      kind: "saving",
      title: "Saving for",
      options: MINTABLE.map((type) => {
        const price = billFor(type)
          .map((row) => `${row.count} ${materialName(row.line, row.grade).toLowerCase()}`)
          .join(" · ");
        const lit = badge.target === type;
        return onClock({
          id: `save-for-${type}`,
          label: PIECE_NAMES[type],
          glyph: PIECE_GLYPH[type],
          icon: PIECE_ICON[type],
          on: lit,
          // Choosing what to save for is never refused for poverty — saving up
          // is the answer to poverty. It waits for your turn, like everything
          // else (§29 U1), which `onClock` says.
          enabled: true,
          reason: `Costs ${price}.`,
          // The bill as swatched rows rather than as the sentence: numbers and
          // swatches, no material words (the readouts' ruling, carried).
          //
          // **The lit tile says more**, because it is the readout the deleted
          // `save-for` headline was: whether the *coalition* can pay for one
          // now, or what it is still short of. An unlit tile is an option —
          // its name, its price and four words; the lit one is the machine's
          // own state, and keeps the ready/short sentence.
          card: lit
            ? savingCard(state, square, badge, type)
            : {
                title: PIECE_NAMES[type],
                body: SAVING_RULE,
                rows: billFor(type).map((row) => ({
                  count: row.count,
                  line: row.line,
                  grade: row.grade
                }))
              },
          run: {
            kind: "command",
            command: {
              type: "set-target",
              square,
              // Clicking the lit one clears it.
              target: badge.target === type ? 0 : type,
              // `facing` omitted on purpose: the reducer only writes it when it
              // is present, so retargeting keeps the dial where it was set.
              owner: localSide
            }
          }
        }, settable, rule);
      })
    });
    // **The per-foundry facing dial is gone from here** (designer ruling, U6
    // review): which way new pawns look is a property of the *forge*, and
    // forging went global at U4 — so the dial lives on the hotbar's armed pawn
    // tile with its `mint-facing-*` ids, and a second copy per machine was two
    // controls for one setting.
  }

  if (badge.kind === "store" && yourPolicy && badge.accept !== undefined) {
    const accept = badge.accept ?? 0;
    const offer = badge.offer ?? 0;
    out.push({
      kind: "taps",
      title: "Store policy",
      rows: GRADE_NAMES.map((_, grade) => ({
        grade,
        name: GRADE_NAMES[grade],
        count: badge.stock?.[grade] ?? 0,
        accept: onClock({
          id: `store-accept-${grade}`,
          label: "In",
          on: (accept & (1 << grade)) !== 0,
          enabled: true,
          reason: TAPS_RULE,
          card: { title: `${GRADE_NAMES[grade]} in`, body: TAPS_RULE },
          run: {
            kind: "command",
            command: {
              type: "set-store-policy",
              square,
              grade: grade as Grade,
              accept: (accept & (1 << grade)) === 0,
              offer: (offer & (1 << grade)) !== 0,
              owner: localSide
            }
          }
        }, settable, rule),
        offer: onClock({
          id: `store-offer-${grade}`,
          label: "Out",
          on: (offer & (1 << grade)) !== 0,
          enabled: true,
          reason: TAPS_RULE,
          card: { title: `${GRADE_NAMES[grade]} out`, body: TAPS_RULE },
          run: {
            kind: "command",
            command: {
              type: "set-store-policy",
              square,
              grade: grade as Grade,
              accept: (accept & (1 << grade)) !== 0,
              offer: (offer & (1 << grade)) === 0,
              owner: localSide
            }
          }
        }, settable, rule)
      }))
    });
  }

  // §31 S8: a cone gun turns with a move — four facings, the current one lit.
  if (badge.kind === "turret" && yourPolicy && badge.turret?.aims) {
    const facing = badge.turret.state.facing;
    out.push({
      kind: "turret",
      title: "Facing",
      rows: DIRECTIONS.map((dir) =>
        onClock({
          id: `turret-aim-${DIRECTION_NAMES[dir]}`,
          label: dir === facing ? `${DIRECTION_NAMES[dir]} · facing` : `${DIRECTION_NAMES[dir]} · 1 move`,
          stat: DIRECTION_NAMES[dir],
          on: dir === facing,
          enabled: dir !== facing,
          reason: dir === facing ? "It already faces that way." : TURRET_RULE,
          card: { title: `Face ${DIRECTION_NAMES[dir]}`, body: TURRET_RULE },
          run: {
            kind: "command",
            command: { type: "aim-turret", square, facing: dir, owner: localSide }
          }
        }, settable, rule)
      )
    });
  }

  if (badge.upgrades && badge.upgrades.length > 0) {
    out.push({
      kind: "climb",
      title: "Climb",
      tiles: badge.upgrades.map((option) => {
        // Always the reason, never a dead button.
        const note = option.reason ?? option.price;
        const why = option.enabled && settable ? CLIMB_RULE : note;
        return {
          // **Keyed by the variant, not by the rank.** Every variant leaves the
          // piece its rank, so keying on `target` would give a pawn's Marshal
          // and DAM the same id.
          id: `variant-${option.variant}`,
          label: option.name,
          glyph: PIECE_GLYPH[option.target],
          icon: PIECE_ICON[option.target],
          note,
          enabled: Boolean(option.enabled) && settable,
          reason: why,
          card: { title: option.name, body: why },
          run: {
            kind: "upgrade",
            square,
            target: option.target,
            ...(option.variant ? { variant: option.variant } : {})
          }
        };
      })
    });
  }

  // **A fuse (§31 P4)**, climb's tiles under its own title: listed once the
  // partners stand by, off and saying why until it can be paid.
  if (badge.fuses && badge.fuses.length > 0) {
    out.push({
      kind: "climb",
      title: "Fuse",
      tiles: badge.fuses.map((option) => {
        const note = option.reason ?? option.price;
        const why = option.enabled && settable ? FUSE_RULE : note;
        return {
          id: `fuse-${option.variant}`,
          label: option.name,
          glyph: PIECE_GLYPH[option.target],
          icon: PIECE_ICON[option.target],
          note,
          enabled: option.enabled && settable,
          reason: why,
          card: { title: option.name, body: why },
          run: { kind: "fuse", parts: option.parts, into: option.variant }
        };
      })
    });
  }

  // **A power is a mechanic, not a button** (§28.3) — Sacrifice surfaces on the
  // piece it would take apart, while the altar stands. It costs the turn, so it
  // is gated on `settable` like every other action; `beginSacrifice` still asks
  // the rules and says why when they refuse.
  if (powers.includes("sacrifice") && yours && settable) {
    out.push({
      kind: "sacrifice",
      item: {
        id: "sacrifice",
        label: "Sacrifice",
        enabled: true,
        reason: SACRIFICE_RULE,
        card: { title: "Sacrifice", body: SACRIFICE_RULE },
        run: { kind: "sacrifice" }
      }
    });
  }

  return out;
}
